import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Fetches available quick replies for a conversation and lets the user
// pick one to fill the message textarea. Supports slash commands:
//   - Typing /command then space/Enter auto-fills the matching quick reply.
//   - Typing / (optionally followed by a partial command) opens the menu and
//     filters the list live as you type, matching on slash_command or name.
//
// Targets:
//   - button: the trigger button that opens the dropdown
//   - menu: the dropdown container
//   - list: the UL element populated with response items
//   - textarea: the message textarea to fill when a response is selected
//
// Values:
//   - url: the endpoint returning rendered quick replies JSON

export default class extends Controller {
  static targets = ["button", "menu", "list", "textarea"]
  static values = { url: String }

  connect() {
    this.open = false
    this.openedViaSlash = false
    this.responses = []
    this.boundClose = this.closeOnOutsideClick.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose)
  }

  async toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.open) {
      this.close()
      return
    }

    await this.fetchResponses()
    this.renderResponses()
    this.openMenu()
  }

  close() {
    this.open = false
    this.openedViaSlash = false
    this.menuTarget.classList.add("hidden")
    document.removeEventListener("click", this.boundClose)
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  select(event) {
    event.preventDefault()
    const content = event.currentTarget.dataset.content
    this.textareaTarget.value = content
    this.textareaTarget.dispatchEvent(new Event("input"))
    this.textareaTarget.focus()
    this.close()
  }

  // Live slash-menu: open + filter while the textarea holds only "/partial".
  async handleInput() {
    const text = this.textareaTarget.value.trim()
    const slashMatch = text.match(/^\/(\S*)$/)

    if (!slashMatch) {
      // Typing a normal message dismisses the menu, however it was opened
      // (slash command or the "More" button).
      if (this.open) this.close()
      return
    }

    if (this.responses.length === 0) {
      await this.fetchResponses()
    }

    const filter = slashMatch[1].toLowerCase()
    this.renderResponses(filter)
    this.openMenu({ viaSlash: true })
  }

  async handleKeydown(event) {
    if (event.key !== " " && event.key !== "Enter") return

    const text = this.textareaTarget.value.trim()
    const slashMatch = text.match(/^\/(\S+)$/)
    if (!slashMatch) return

    const command = slashMatch[1].toLowerCase()

    if (this.responses.length === 0) {
      await this.fetchResponses()
    }

    const match = this.responses.find((r) => r.slash_command === command)

    if (match) {
      event.preventDefault()
      this.textareaTarget.value = match.content
      this.textareaTarget.dispatchEvent(new Event("input"))
      this.textareaTarget.focus()
      this.close()
    }
    // No exact command match: leave the keystroke alone so the advocate can type a
    // normal message that begins with "/" (e.g. "/r 5 left"). Browsing is handled
    // by the live slash menu in handleInput as they type.
  }

  openMenu({ viaSlash = false } = {}) {
    this.open = true
    this.openedViaSlash = viaSlash
    this.menuTarget.classList.remove("hidden")
    document.addEventListener("click", this.boundClose)
  }

  async fetchResponses() {
    try {
      const response = await get(this.urlValue, { responseKind: "json" })

      if (!response.ok) return

      this.responses = await response.json
    } catch {
      // silently fail
    }
  }

  filteredResponses(filter) {
    if (!filter) return this.responses

    return this.responses.filter(
      (r) =>
        r.slash_command.toLowerCase().includes(filter) ||
        r.name.toLowerCase().includes(filter)
    )
  }

  renderResponses(filter = "") {
    const responses = this.filteredResponses(filter)

    if (responses.length === 0) {
      this.listTarget.innerHTML = `
        <li class="px-4 py-3 text-sm text-gray-500 dark:text-neutral-400">
          No quick replies available.
        </li>
      `
      return
    }

    this.listTarget.innerHTML = responses
      .map(
        (resp) => `
      <li>
        <button type="button"
          class="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-neutral-800 focus:bg-gray-100 dark:focus:bg-neutral-800 transition-colors"
          data-action="quick-reply-picker#select"
          data-content="${this.escapeAttr(resp.content)}">
          <div class="flex items-center gap-2">
            <span class="text-gray-400 dark:text-neutral-500 shrink-0">${resp.icon_svg}</span>
            <span class="font-mono text-xs text-blue-600 dark:text-blue-300">/${this.escapeAttr(resp.slash_command)}</span>
            <span class="font-medium text-gray-800 dark:text-neutral-200">${this.escapeAttr(resp.name)}</span>
          </div>
          <div class="text-xs text-gray-500 dark:text-neutral-400 truncate mt-0.5">${this.escapeAttr(resp.content)}</div>
        </button>
      </li>
    `
      )
      .join("")
  }

  escapeAttr(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
